import { NotePencil, PaperPlaneTilt } from '@phosphor-icons/react';
import { useEffect, useId, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { useOptionalWorkspace } from '@/app/providers/workspace-provider';
import type { SaveCoordinator } from '@/data/save-coordinator';
import { createTaskRepository } from '@/data/repositories/task-repository';
import type { Task } from '@/data/db';
import { cn } from '@/lib/cn';

/**
 * 卡片與抽屜上的快速更新（spec 13.5）。
 *
 * 快速新增只留得下一個標題，之後要補內容不該每次都得開詳細抽屜再找欄位。
 * 這裡讓標題、一筆進度與完成度都能直接在卡片上改完就存。
 */

/** 完成度的快速選項；要的是「大概到哪了」，不是精準百分比。 */
export const PROGRESS_CHOICES = [0, 25, 50, 75, 100] as const;

const INPUT_CLASS =
  'w-full rounded-[var(--radius-control)] border border-border bg-surface px-2.5 py-1.5 ' +
  'text-[0.8125rem] text-text placeholder:text-text-muted/70 ' +
  'focus:border-accent focus:outline-none';

function describeError(error: unknown): string {
  return error instanceof Error ? error.message : '請再試一次。';
}

/** 走自動保存協調器，狀態列才會跟著跑（spec 6.2）。 */
async function withSave<T>(save: SaveCoordinator, operation: () => Promise<T>): Promise<T> {
  let outcome!: T;
  await save.run(async () => {
    outcome = await operation();
  });
  return outcome;
}

/** 卡片上的快速更新入口；沒有資料層（例如拖曳浮層）時什麼都不顯示。 */
export function TaskQuickUpdate({ task }: { task: Task }) {
  const workspace = useOptionalWorkspace();
  const [open, setOpen] = useState(false);

  if (!workspace) return null;

  if (!open) {
    return (
      <button
        type="button"
        aria-label={`更新 ${task.subject} 的標題或進度`}
        onClick={() => setOpen(true)}
        className={cn(
          'mt-2 flex w-full items-center justify-center gap-1.5 rounded-[var(--radius-control)]',
          'border border-dashed border-border px-2 py-1.5 text-[0.75rem] text-text-muted',
          'transition-colors duration-200 hover:border-accent hover:text-accent',
        )}
      >
        <NotePencil size={13} aria-hidden="true" />
        記一筆進度或改標題
      </button>
    );
  }

  return <QuickUpdateForm task={task} onClose={() => setOpen(false)} />;
}

function QuickUpdateForm({ task, onClose }: { task: Task; onClose: () => void }) {
  const workspace = useOptionalWorkspace();
  const repository = useMemo(
    () => (workspace ? createTaskRepository(workspace.db) : null),
    [workspace],
  );

  const [subject, setSubject] = useState(task.subject);
  const [note, setNote] = useState('');
  const [progress, setProgress] = useState<number | null>(task.progress);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const noteRef = useRef<HTMLTextAreaElement | null>(null);
  const fieldId = useId();

  useEffect(() => {
    noteRef.current?.focus();
  }, []);

  // 便條沒有完成度欄位（data-model T5）。
  const showProgress = task.kind === 'task';

  async function handleSubmit(): Promise<void> {
    if (!workspace || !repository || busy) return;

    const trimmed = subject.trim();
    if (!trimmed) {
      setError('標題不能空白，原本的標題還留著。');
      return;
    }

    setBusy(true);
    setError(null);
    try {
      // 先把別處還在 debounce 的說明寫出去，否則它晚一步落地會蓋掉剛記的進度。
      await workspace.save.flush();
      await workspace.save.run(async () => {
        if (trimmed !== task.subject) {
          await repository.update(task.id, { subject: trimmed });
        }
        if (showProgress && progress !== task.progress) {
          await repository.update(task.id, { progress });
        }
        if (note.trim()) {
          await repository.appendProgress(task.id, note);
        }
      });
      // 存成功才關；失敗時輸入的字一律留在畫面上，絕不假成功（spec 6.2）。
      setNote('');
      onClose();
    } catch (caught) {
      setError(`沒有存成功：${describeError(caught)}`);
    } finally {
      setBusy(false);
    }
  }

  function handleKeyDown(event: KeyboardEvent<HTMLElement>): void {
    if (event.key === 'Escape') {
      event.preventDefault();
      onClose();
      return;
    }
    // Enter 直接存，Shift+Enter 才換行。
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      void handleSubmit();
    }
  }

  return (
    <form
      className="mt-2 space-y-2 rounded-[var(--radius-control-lg)] border border-border bg-surface-sunken/70 p-2"
      onSubmit={(event) => {
        event.preventDefault();
        void handleSubmit();
      }}
    >
      <label className="sr-only" htmlFor={`${fieldId}-subject`}>
        標題
      </label>
      <input
        id={`${fieldId}-subject`}
        value={subject}
        onChange={(event) => setSubject(event.target.value)}
        onKeyDown={handleKeyDown}
        aria-invalid={subject.trim() ? undefined : true}
        className={INPUT_CLASS}
      />

      <label className="sr-only" htmlFor={`${fieldId}-note`}>
        記一筆進度
      </label>
      <textarea
        id={`${fieldId}-note`}
        ref={noteRef}
        rows={2}
        value={note}
        onChange={(event) => setNote(event.target.value)}
        onKeyDown={handleKeyDown}
        placeholder="做到哪了？Enter 存起來、Shift+Enter 換行"
        className={cn(INPUT_CLASS, 'resize-y leading-relaxed')}
      />

      {showProgress && (
        <fieldset className="flex flex-wrap items-center gap-1">
          <legend className="sr-only">完成度</legend>
          {PROGRESS_CHOICES.map((choice) => (
            <button
              key={choice}
              type="button"
              aria-pressed={progress === choice}
              onClick={() => setProgress(progress === choice ? null : choice)}
              className={cn(
                'tabular rounded-full px-2 py-0.5 text-[0.6875rem] transition-colors duration-200',
                progress === choice
                  ? 'bg-accent text-white'
                  : 'bg-surface-raised text-text-muted hover:text-text',
              )}
            >
              {choice}%
            </button>
          ))}
        </fieldset>
      )}

      <div className="flex items-center gap-1.5">
        <button
          type="submit"
          disabled={busy}
          className={cn(
            'inline-flex items-center gap-1 rounded-full bg-accent px-3 py-1',
            'text-[0.75rem] font-medium text-white transition-colors duration-200',
            'hover:bg-accent-strong disabled:opacity-50',
          )}
        >
          <PaperPlaneTilt size={12} weight="fill" aria-hidden="true" />
          存起來
        </button>
        <button
          type="button"
          onClick={onClose}
          className="rounded-full px-2.5 py-1 text-[0.75rem] text-text-muted hover:text-text"
        >
          取消
        </button>
      </div>

      {error && (
        <p role="alert" className="text-[0.6875rem] text-danger">
          {error}
        </p>
      )}
    </form>
  );
}

/** 詳細抽屜用的「記一筆進度」；存好之後把新的說明回拋給抽屜的草稿。 */
export function ProgressQuickAdd({
  task,
  onSaved,
}: {
  task: Task;
  onSaved: (description: string | null) => void;
}) {
  const workspace = useOptionalWorkspace();
  const repository = useMemo(
    () => (workspace ? createTaskRepository(workspace.db) : null),
    [workspace],
  );

  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const fieldId = useId();

  async function handleSubmit(): Promise<void> {
    if (!workspace || !repository || busy || !note.trim()) return;

    setBusy(true);
    setError(null);
    try {
      // 說明欄可能還有 debounce 中的文字，先寫出去再接進度。
      await workspace.save.flush();
      const updated = await withSave(workspace.save, () =>
        repository.appendProgress(task.id, note),
      );
      setNote('');
      onSaved(updated.description);
    } catch (caught) {
      setError(`沒有存成功：${describeError(caught)}`);
    } finally {
      setBusy(false);
    }
  }

  if (!workspace || !repository) return null;

  return (
    <div className="space-y-1.5 rounded-[var(--radius-control-lg)] bg-surface-sunken px-3 py-2.5">
      <label className="block text-[0.8125rem] font-medium text-text-muted" htmlFor={fieldId}>
        記一筆進度
      </label>
      <div className="flex items-start gap-2">
        <textarea
          id={fieldId}
          rows={2}
          value={note}
          onChange={(event) => setNote(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter' && !event.shiftKey) {
              event.preventDefault();
              void handleSubmit();
            }
          }}
          placeholder="做到哪了？Enter 存起來、Shift+Enter 換行"
          className={cn(INPUT_CLASS, 'resize-y leading-relaxed')}
        />
        <button
          type="button"
          onClick={() => void handleSubmit()}
          disabled={busy || !note.trim()}
          className={cn(
            'inline-flex shrink-0 items-center gap-1 rounded-full bg-accent px-3 py-1.5',
            'text-[0.75rem] font-medium text-white transition-colors duration-200',
            'hover:bg-accent-strong disabled:opacity-45',
          )}
        >
          <PaperPlaneTilt size={12} weight="fill" aria-hidden="true" />
          記下來
        </button>
      </div>
      <p className="text-[0.6875rem] text-text-muted">會加在說明最後面，並自動標上現在的時間。</p>
      {error && (
        <p role="alert" className="text-[0.6875rem] text-danger">
          {error}
        </p>
      )}
    </div>
  );
}
